/**
 * Raptor Predator Frontend - Loading Screen
 * 
 * Full-screen loading indicator with animated Raptor logo.
 */

import { motion } from 'framer-motion';
import { Shield } from 'lucide-react';

interface LoadingScreenProps {
  message?: string;
}

export function LoadingScreen({ message = 'Loading...' }: LoadingScreenProps): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900">
      <div className="flex flex-col items-center gap-6">
        {/* Animated logo */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="relative"
        >
          <motion.div
            animate={{
              boxShadow: [
                '0 0 20px 0px rgba(6, 182, 212, 0.3)',
                '0 0 40px 10px rgba(6, 182, 212, 0.5)',
                '0 0 20px 0px rgba(6, 182, 212, 0.3)',
              ],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-cyan-700"
          >
            <Shield className="h-10 w-10 text-white" />
          </motion.div>
        </motion.div>

        {/* Loading spinner */}
        <div className="relative h-12 w-12">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'linear',
            }}
            className="absolute inset-0 rounded-full border-2 border-transparent border-t-cyan-500"
          />
          <motion.div
            animate={{ rotate: -360 }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'linear',
            }}
            className="absolute inset-1 rounded-full border-2 border-transparent border-t-cyan-300"
          />
        </div>

        {/* Message */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-sm text-slate-400"
        >
          {message}
        </motion.p>
      </div>
    </div>
  );
}

export default LoadingScreen;

